/**
 * Industry Benchmarks Configuration - COMPLETE
 * =============================================
 * Authoritative benchmarks for ALL 24 industry categories
 * Sources: ATO Small Business Benchmarks, ABS, IBISWorld, Industry Associations
 * 
 * DATA FRESHNESS:
 * - ATO benchmarks updated annually (last update: FY2023-24)
 * - Industry association data updated quarterly
 * - Corvana aggregated data updated in real-time from connected clients
 */

// ============================================
// DATA SOURCES - All trusted sources
// ============================================
export const DATA_SOURCES = {
  // === LIVE INTEGRATIONS (Real-time from client accounts) ===
  xero: {
    id: 'xero',
    name: 'Xero',
    type: 'live_integration',
    logo: '/logos/xero.svg',
    color: '#13B5EA',
    trustBadge: 'Live Data',
    description: 'Real-time financial data synced from your Xero account',
    url: 'https://www.xero.com',
    isLive: true,
    refreshRate: 'Every 15 minutes',
  },
  myob: {
    id: 'myob',
    name: 'MYOB',
    type: 'live_integration',
    logo: '/logos/myob.svg',
    color: '#6B2D90',
    trustBadge: 'Live Data',
    description: 'Real-time financial data synced from your MYOB account',
    url: 'https://www.myob.com',
    isLive: true,
    refreshRate: 'Every 15 minutes',
  },
  quickbooks: {
    id: 'quickbooks',
    name: 'QuickBooks',
    type: 'live_integration',
    logo: '/logos/quickbooks.svg',
    color: '#2CA01C',
    trustBadge: 'Live Data',
    description: 'Real-time financial data synced from QuickBooks',
    url: 'https://quickbooks.intuit.com',
    isLive: true,
    refreshRate: 'Every 15 minutes',
  },
  square: {
    id: 'square',
    name: 'Square',
    type: 'live_integration',
    logo: '/logos/square.svg',
    color: '#006AFF',
    trustBadge: 'Live Data',
    description: 'Live sales and transaction data from Square POS',
    url: 'https://squareup.com',
    isLive: true,
    refreshRate: 'Real-time',
  },
  kounta: {
    id: 'kounta',
    name: 'Kounta',
    type: 'live_integration',
    logo: '/logos/kounta.svg',
    color: '#00BCD4',
    trustBadge: 'Live Data',
    description: 'Real-time sales data from Kounta POS',
    url: 'https://www.kounta.com',
    isLive: true,
    refreshRate: 'Real-time',
  },
  shopify: {
    id: 'shopify',
    name: 'Shopify',
    type: 'live_integration',
    logo: '/logos/shopify.svg',
    color: '#96BF48',
    trustBadge: 'Live Data',
    description: 'Real-time e-commerce data from Shopify',
    url: 'https://www.shopify.com.au',
    isLive: true,
    refreshRate: 'Every 5 minutes',
  },
  google_analytics: {
    id: 'google_analytics',
    name: 'Google Analytics',
    type: 'live_integration',
    logo: '/logos/ga.svg',
    color: '#F9AB00',
    trustBadge: 'Live Data',
    description: 'Website traffic and conversion analytics',
    url: 'https://analytics.google.com',
    isLive: true,
    refreshRate: 'Hourly',
  },
  deputy: {
    id: 'deputy',
    name: 'Deputy',
    type: 'live_integration',
    logo: '/logos/deputy.svg',
    color: '#00AEEF',
    trustBadge: 'Live Data',
    description: 'Staff scheduling and time tracking',
    url: 'https://www.deputy.com',
    isLive: true,
    refreshRate: 'Real-time',
  },
  tanda: {
    id: 'tanda',
    name: 'Tanda',
    type: 'live_integration',
    logo: '/logos/deputy.svg',
    color: '#2CC2B9',
    trustBadge: 'Live Data',
    description: 'Workforce rostering, time & attendance',
    url: 'https://www.tanda.co',
    isLive: true,
    refreshRate: 'Real-time',
  },
  
  // === BENCHMARK SOURCES (Updated periodically) ===
  ato: {
    id: 'ato',
    name: 'Australian Taxation Office',
    type: 'benchmark',
    logo: '/logos/ato.svg',
    color: '#00698F',
    trustBadge: 'Official Data',
    description: 'ATO Small Business Benchmarks - official tax and business benchmarks',
    url: 'https://www.ato.gov.au/Business/Small-business-benchmarks/',
    isLive: false,
    refreshRate: 'Annual (FY2023-24)',
    lastUpdated: '2024-07-01',
  },
  abs: {
    id: 'abs',
    name: 'Australian Bureau of Statistics',
    type: 'benchmark',
    logo: '/logos/abs.svg',
    color: '#00529B',
    trustBadge: 'Official Data',
    description: 'Official Australian government statistics',
    url: 'https://www.abs.gov.au',
    isLive: false,
    refreshRate: 'Quarterly',
    lastUpdated: '2024-12-01',
  },
  ibisworld: {
    id: 'ibisworld',
    name: 'IBISWorld',
    type: 'benchmark',
    logo: '/logos/ibisworld.svg',
    color: '#003366',
    trustBadge: 'Industry Research',
    description: 'Leading industry market research provider',
    url: 'https://www.ibisworld.com',
    isLive: false,
    refreshRate: 'Quarterly',
    lastUpdated: '2024-12-15',
  },
  
  // === INDUSTRY ASSOCIATIONS ===
  restaurant_catering: {
    id: 'restaurant_catering',
    name: 'Restaurant & Catering Australia',
    type: 'industry_body',
    logo: '/logos/rca.svg',
    color: '#B8860B',
    trustBadge: 'Industry Body',
    description: 'National hospitality industry association',
    url: 'https://rfrca.com.au',
    isLive: false,
    refreshRate: 'Quarterly',
  },
  ara: {
    id: 'ara',
    name: 'Australian Retailers Association',
    type: 'industry_body',
    logo: '/logos/ara.svg',
    color: '#E63946',
    trustBadge: 'Industry Body',
    description: 'National retail industry association',
    url: 'https://www.retail.org.au',
    isLive: false,
    refreshRate: 'Monthly',
  },
  hia: {
    id: 'hia',
    name: 'Housing Industry Association',
    type: 'industry_body',
    logo: '/logos/hia.svg',
    color: '#1E4D92',
    trustBadge: 'Industry Body',
    description: 'Australia\'s peak residential building industry body',
    url: 'https://hia.com.au',
    isLive: false,
    refreshRate: 'Quarterly',
  },
  ahpra: {
    id: 'ahpra',
    name: 'AHPRA',
    type: 'regulatory',
    logo: '/logos/ahpra.svg',
    color: '#00A3E0',
    trustBadge: 'Regulatory Body',
    description: 'Australian Health Practitioner Regulation Agency',
    url: 'https://www.ahpra.gov.au',
    isLive: false,
    refreshRate: 'Annual',
  },
  ava: {
    id: 'ava',
    name: 'Australian Veterinary Association',
    type: 'industry_body',
    logo: '/logos/ava.svg',
    color: '#2E7D32',
    trustBadge: 'Industry Body',
    description: 'Peak body for veterinary professionals',
    url: 'https://www.ava.com.au',
    isLive: false,
    refreshRate: 'Annual',
  },
  aia: {
    id: 'aia',
    name: 'Australian Institute of Architects',
    type: 'industry_body',
    logo: '/logos/aia.svg',
    color: '#1A237E',
    trustBadge: 'Industry Body',
    description: 'Peak body for architecture profession',
    url: 'https://www.architecture.com.au',
    isLive: false,
    refreshRate: 'Annual',
  },
  fitness_australia: {
    id: 'fitness_australia',
    name: 'Fitness Australia',
    type: 'industry_body',
    logo: '/logos/fitness.svg',
    color: '#FF5722',
    trustBadge: 'Industry Body',
    description: 'Peak health and fitness industry body',
    url: 'https://fitness.org.au',
    isLive: false,
    refreshRate: 'Quarterly',
  },
  ata: {
    id: 'ata',
    name: 'Australian Trucking Association',
    type: 'industry_body',
    logo: '/logos/ata.svg',
    color: '#455A64',
    trustBadge: 'Industry Body',
    description: 'Peak body for trucking industry',
    url: 'https://www.truck.net.au',
    isLive: false,
    refreshRate: 'Quarterly',
  },
  amic: {
    id: 'amic',
    name: 'Australian Meat Industry Council',
    type: 'industry_body',
    logo: '/logos/amic.svg',
    color: '#8D6E63',
    trustBadge: 'Industry Body',
    description: 'Peak body for meat processing industry',
    url: 'https://amic.org.au',
    isLive: false,
    refreshRate: 'Quarterly',
  },
  
  // === AGGREGATED CORVANA DATA ===
  corvana_aggregate: {
    id: 'corvana_aggregate',
    name: 'Corvana Industry Average',
    type: 'aggregated',
    logo: '/corvana-logo-128.webp',
    color: '#6366F1',
    trustBadge: 'Live Aggregate',
    description: 'Real-time anonymized averages from Corvana clients in your industry',
    url: null,
    isLive: true,
    refreshRate: 'Daily',
    note: 'Based on anonymized data from 2,500+ Australian businesses',
  },
  corvana_ai: {
    id: 'corvana_ai',
    name: 'Corvana AI',
    type: 'ai',
    logo: '/corvana-logo-128.webp',
    color: '#8B5CF6',
    trustBadge: 'AI Analysis',
    description: 'AI-powered insights from your business data',
    url: null,
    isLive: true,
    refreshRate: 'Real-time',
  },
};

// ============================================
// COMPLETE INDUSTRY BENCHMARKS - ALL 24 CATEGORIES
// ============================================
export const INDUSTRY_BENCHMARKS = {
  // ========== 1. HOSPITALITY ==========
  hospitality: {
    industry: 'Hospitality',
    atoCode: 'H',
    description: 'Restaurants, cafes, bars, catering, and food service',
    benchmarks: {
      food_cost_percentage: {
        metric: 'Food Cost %',
        benchmark: 28,
        range: { low: 25, high: 35 },
        unit: '%',
        source: 'ato',
        sourceNote: 'ATO Small Business Benchmarks - Cafes & Restaurants',
        goodDirection: 'lower',
        description: 'Cost of ingredients as percentage of food sales',
        kpiId: 'food_cost',
      },
      labor_cost_percentage: {
        metric: 'Labor Cost %',
        benchmark: 32,
        range: { low: 28, high: 38 },
        unit: '%',
        source: 'restaurant_catering',
        sourceNote: 'R&CA Labor Cost Survey 2024',
        goodDirection: 'lower',
        description: 'Staff wages as percentage of revenue',
        kpiId: 'labor_cost',
      },
      prime_cost: {
        metric: 'Prime Cost %',
        benchmark: 60,
        range: { low: 55, high: 65 },
        unit: '%',
        source: 'ibisworld',
        sourceNote: 'IBISWorld Cafes & Restaurants in Australia',
        goodDirection: 'lower',
        description: 'Food + Labor as percentage of revenue',
        kpiId: 'prime_cost',
      },
      gross_profit_margin: {
        metric: 'Gross Margin',
        benchmark: 65,
        range: { low: 58, high: 72 },
        unit: '%',
        source: 'ato',
        sourceNote: 'ATO Benchmarks FY2023-24',
        goodDirection: 'higher',
        description: 'Revenue minus cost of goods sold',
        kpiId: 'gross_margin',
      },
      table_turnover: {
        metric: 'Table Turnover',
        benchmark: 2.5,
        range: { low: 1.8, high: 4.0 },
        unit: 'x/day',
        source: 'corvana_aggregate',
        sourceNote: 'Corvana client average',
        goodDirection: 'higher',
        description: 'Average table turns per service period',
        kpiId: 'table_turns',
      },
      average_check: {
        metric: 'Average Check',
        benchmark: 45,
        range: { low: 25, high: 85 },
        unit: '$',
        source: 'abs',
        sourceNote: 'ABS Retail Trade Survey',
        goodDirection: 'higher',
        description: 'Average spend per customer',
        kpiId: 'avg_check',
      },
      wastage_percentage: {
        metric: 'Wastage %',
        benchmark: 4,
        range: { low: 2, high: 8 },
        unit: '%',
        source: 'restaurant_catering',
        sourceNote: 'R&CA Sustainability Report',
        goodDirection: 'lower',
        description: 'Food waste as percentage of purchases',
        kpiId: 'wastage',
      },
      customer_satisfaction: {
        metric: 'Customer Rating',
        benchmark: 4.2,
        range: { low: 3.8, high: 4.8 },
        unit: '/5',
        source: 'corvana_ai',
        sourceNote: 'Aggregated from Google, TripAdvisor',
        goodDirection: 'higher',
        description: 'Average rating across review platforms',
        kpiId: 'csat',
      },
    },
  },

  // ========== 2. HEALTHCARE ==========
  healthcare: {
    industry: 'Healthcare',
    atoCode: 'Q',
    description: 'Medical practices, clinics, allied health, dental',
    benchmarks: {
      gross_profit_margin: {
        metric: 'Gross Margin',
        benchmark: 45,
        range: { low: 38, high: 55 },
        unit: '%',
        source: 'ato',
        sourceNote: 'ATO Benchmarks - Health Care',
        goodDirection: 'higher',
        description: 'Revenue minus direct costs',
        kpiId: 'gross_margin',
      },
      patient_wait_time: {
        metric: 'Avg Wait Time',
        benchmark: 15,
        range: { low: 5, high: 30 },
        unit: 'min',
        source: 'ahpra',
        sourceNote: 'AHPRA Practice Standards',
        goodDirection: 'lower',
        description: 'Average patient wait time from arrival',
        kpiId: 'wait_time',
      },
      appointment_utilization: {
        metric: 'Appointment Utilization',
        benchmark: 85,
        range: { low: 75, high: 95 },
        unit: '%',
        source: 'ibisworld',
        sourceNote: 'IBISWorld Medical Practices',
        goodDirection: 'higher',
        description: 'Percentage of available slots filled',
        kpiId: 'utilization',
      },
      no_show_rate: {
        metric: 'No-Show Rate',
        benchmark: 8,
        range: { low: 3, high: 15 },
        unit: '%',
        source: 'corvana_aggregate',
        sourceNote: 'Corvana healthcare clients',
        goodDirection: 'lower',
        description: 'Percentage of appointments missed',
        kpiId: 'no_show',
      },
      revenue_per_provider: {
        metric: 'Revenue/Provider',
        benchmark: 450000,
        range: { low: 300000, high: 750000 },
        unit: '$/year',
        source: 'ato',
        sourceNote: 'ATO Small Business Benchmarks',
        goodDirection: 'higher',
        description: 'Annual revenue per practitioner',
        kpiId: 'rev_provider',
      },
      collection_rate: {
        metric: 'Collection Rate',
        benchmark: 95,
        range: { low: 88, high: 99 },
        unit: '%',
        source: 'ibisworld',
        sourceNote: 'IBISWorld Healthcare Financial',
        goodDirection: 'higher',
        description: 'Percentage of billed revenue collected',
        kpiId: 'collection',
      },
      patient_retention: {
        metric: 'Patient Retention',
        benchmark: 80,
        range: { low: 65, high: 92 },
        unit: '%',
        source: 'corvana_ai',
        sourceNote: 'Calculated from booking patterns',
        goodDirection: 'higher',
        description: 'Patients returning within 12 months',
        kpiId: 'retention',
      },
    },
  },

  // ========== 3. VETERINARY ==========
  veterinary: {
    industry: 'Veterinary Services',
    atoCode: 'Q',
    description: 'Veterinary clinics, animal hospitals, pet care',
    benchmarks: {
      gross_profit_margin: {
        metric: 'Gross Margin',
        benchmark: 42,
        range: { low: 35, high: 52 },
        unit: '%',
        source: 'ato',
        sourceNote: 'ATO Benchmarks - Veterinary Services',
        goodDirection: 'higher',
        description: 'Revenue minus direct costs',
        kpiId: 'gross_margin',
      },
      average_transaction: {
        metric: 'Avg Transaction',
        benchmark: 185,
        range: { low: 120, high: 350 },
        unit: '$',
        source: 'ava',
        sourceNote: 'AVA Practice Benchmarks 2024',
        goodDirection: 'higher',
        description: 'Average spend per visit',
        kpiId: 'avg_transaction',
      },
      labor_cost_percentage: {
        metric: 'Labor Cost %',
        benchmark: 45,
        range: { low: 38, high: 52 },
        unit: '%',
        source: 'ava',
        sourceNote: 'AVA Employment Survey',
        goodDirection: 'lower',
        description: 'Staff wages as percentage of revenue',
        kpiId: 'labor_cost',
      },
      client_retention: {
        metric: 'Client Retention',
        benchmark: 75,
        range: { low: 60, high: 88 },
        unit: '%',
        source: 'corvana_aggregate',
        sourceNote: 'Corvana vet clients',
        goodDirection: 'higher',
        description: 'Clients returning within 12 months',
        kpiId: 'retention',
      },
      appointments_per_vet: {
        metric: 'Appointments/Vet/Day',
        benchmark: 18,
        range: { low: 12, high: 25 },
        unit: '/day',
        source: 'ava',
        sourceNote: 'AVA Productivity Survey',
        goodDirection: 'higher',
        description: 'Daily appointments per veterinarian',
        kpiId: 'appts_per_vet',
      },
    },
  },

  // ========== 4. WHOLESALE & DISTRIBUTION ==========
  wholesale: {
    industry: 'Wholesale & Distribution',
    atoCode: 'F',
    description: 'Wholesalers, distributors, B2B suppliers',
    benchmarks: {
      gross_profit_margin: {
        metric: 'Gross Margin',
        benchmark: 22,
        range: { low: 15, high: 35 },
        unit: '%',
        source: 'ato',
        sourceNote: 'ATO Benchmarks - Wholesale Trade',
        goodDirection: 'higher',
        description: 'Revenue minus cost of goods sold',
        kpiId: 'gross_margin',
      },
      inventory_turnover: {
        metric: 'Inventory Turnover',
        benchmark: 6,
        range: { low: 4, high: 12 },
        unit: 'x/year',
        source: 'abs',
        sourceNote: 'ABS Wholesale Trade Survey',
        goodDirection: 'higher',
        description: 'Times inventory sold and replaced annually',
        kpiId: 'inv_turnover',
      },
      fill_rate: {
        metric: 'Order Fill Rate',
        benchmark: 95,
        range: { low: 90, high: 99 },
        unit: '%',
        source: 'ibisworld',
        sourceNote: 'IBISWorld Distribution Efficiency',
        goodDirection: 'higher',
        description: 'Orders shipped complete on first attempt',
        kpiId: 'fill_rate',
      },
      days_sales_outstanding: {
        metric: 'Days Sales Outstanding',
        benchmark: 35,
        range: { low: 21, high: 60 },
        unit: 'days',
        source: 'ato',
        sourceNote: 'ATO Small Business Benchmarks',
        goodDirection: 'lower',
        description: 'Average days to collect payment',
        kpiId: 'dso',
      },
      stockout_rate: {
        metric: 'Stockout Rate',
        benchmark: 3,
        range: { low: 1, high: 8 },
        unit: '%',
        source: 'corvana_aggregate',
        sourceNote: 'Corvana wholesale clients',
        goodDirection: 'lower',
        description: 'Percentage of SKUs out of stock',
        kpiId: 'stockout',
      },
      operating_expense_ratio: {
        metric: 'Operating Expense %',
        benchmark: 18,
        range: { low: 12, high: 25 },
        unit: '%',
        source: 'ato',
        sourceNote: 'ATO Benchmarks FY2023-24',
        goodDirection: 'lower',
        description: 'Operating costs as percentage of revenue',
        kpiId: 'opex_ratio',
      },
    },
  },

  // ========== 5. RETAIL ==========
  retail: {
    industry: 'Retail',
    atoCode: 'G',
    description: 'Retail stores, boutiques, specialty retail',
    benchmarks: {
      gross_profit_margin: {
        metric: 'Gross Margin',
        benchmark: 45,
        range: { low: 30, high: 60 },
        unit: '%',
        source: 'ato',
        sourceNote: 'ATO Benchmarks - Retail Trade',
        goodDirection: 'higher',
        description: 'Revenue minus cost of goods sold',
        kpiId: 'gross_margin',
      },
      conversion_rate: {
        metric: 'Conversion Rate',
        benchmark: 20,
        range: { low: 10, high: 40 },
        unit: '%',
        source: 'ara',
        sourceNote: 'ARA Retail Performance Report',
        goodDirection: 'higher',
        description: 'Visitors who make a purchase',
        kpiId: 'conversion',
      },
      average_transaction: {
        metric: 'Avg Transaction',
        benchmark: 65,
        range: { low: 30, high: 150 },
        unit: '$',
        source: 'abs',
        sourceNote: 'ABS Retail Trade Australia',
        goodDirection: 'higher',
        description: 'Average spend per transaction',
        kpiId: 'avg_transaction',
      },
      sales_per_sqm: {
        metric: 'Sales per m²',
        benchmark: 8500,
        range: { low: 4000, high: 15000 },
        unit: '$/m²/year',
        source: 'ara',
        sourceNote: 'ARA Performance Benchmarks',
        goodDirection: 'higher',
        description: 'Annual revenue per square meter',
        kpiId: 'sales_sqm',
      },
      inventory_shrinkage: {
        metric: 'Shrinkage',
        benchmark: 1.5,
        range: { low: 0.5, high: 3 },
        unit: '%',
        source: 'ara',
        sourceNote: 'ARA Loss Prevention Survey',
        goodDirection: 'lower',
        description: 'Inventory loss from theft, damage, errors',
        kpiId: 'shrinkage',
      },
      inventory_turnover: {
        metric: 'Inventory Turnover',
        benchmark: 4,
        range: { low: 2, high: 8 },
        unit: 'x/year',
        source: 'ato',
        sourceNote: 'ATO Retail Benchmarks',
        goodDirection: 'higher',
        description: 'Times inventory sold annually',
        kpiId: 'inv_turnover',
      },
    },
  },

  // ========== 6. E-COMMERCE ==========
  ecommerce: {
    industry: 'E-Commerce',
    atoCode: 'G',
    description: 'Online retail, digital commerce, D2C brands',
    benchmarks: {
      gross_profit_margin: {
        metric: 'Gross Margin',
        benchmark: 42,
        range: { low: 28, high: 60 },
        unit: '%',
        source: 'ato',
        sourceNote: 'ATO Benchmarks - Online Retail',
        goodDirection: 'higher',
        description: 'Revenue minus COGS and shipping',
        kpiId: 'gross_margin',
      },
      conversion_rate: {
        metric: 'Conversion Rate',
        benchmark: 2.5,
        range: { low: 1, high: 5 },
        unit: '%',
        source: 'ibisworld',
        sourceNote: 'IBISWorld Online Retail',
        goodDirection: 'higher',
        description: 'Visitors who complete purchase',
        kpiId: 'conversion',
      },
      cart_abandonment: {
        metric: 'Cart Abandonment',
        benchmark: 70,
        range: { low: 55, high: 82 },
        unit: '%',
        source: 'corvana_aggregate',
        sourceNote: 'Corvana e-commerce clients',
        goodDirection: 'lower',
        description: 'Carts abandoned before checkout',
        kpiId: 'abandonment',
      },
      customer_acquisition_cost: {
        metric: 'Customer Acquisition Cost',
        benchmark: 45,
        range: { low: 20, high: 120 },
        unit: '$',
        source: 'ibisworld',
        sourceNote: 'IBISWorld Digital Marketing',
        goodDirection: 'lower',
        description: 'Cost to acquire new customer',
        kpiId: 'cac',
      },
      average_order_value: {
        metric: 'Avg Order Value',
        benchmark: 85,
        range: { low: 45, high: 200 },
        unit: '$',
        source: 'abs',
        sourceNote: 'ABS Online Retail Survey',
        goodDirection: 'higher',
        description: 'Average order value',
        kpiId: 'aov',
      },
      return_rate: {
        metric: 'Return Rate',
        benchmark: 12,
        range: { low: 5, high: 25 },
        unit: '%',
        source: 'ara',
        sourceNote: 'ARA Returns Survey',
        goodDirection: 'lower',
        description: 'Orders returned',
        kpiId: 'return_rate',
      },
    },
  },

  // ========== 7. PROFESSIONAL SERVICES ==========
  professional: {
    industry: 'Professional Services',
    atoCode: 'M',
    description: 'Law firms, accounting, consulting, agencies',
    benchmarks: {
      gross_profit_margin: {
        metric: 'Gross Margin',
        benchmark: 65,
        range: { low: 50, high: 80 },
        unit: '%',
        source: 'ato',
        sourceNote: 'ATO Benchmarks - Professional Services',
        goodDirection: 'higher',
        description: 'Revenue minus direct costs',
        kpiId: 'gross_margin',
      },
      utilization_rate: {
        metric: 'Utilization Rate',
        benchmark: 75,
        range: { low: 60, high: 85 },
        unit: '%',
        source: 'ibisworld',
        sourceNote: 'IBISWorld Professional Services',
        goodDirection: 'higher',
        description: 'Billable hours as % of available hours',
        kpiId: 'utilization',
      },
      realization_rate: {
        metric: 'Realization Rate',
        benchmark: 90,
        range: { low: 80, high: 98 },
        unit: '%',
        source: 'ibisworld',
        sourceNote: 'IBISWorld Legal & Accounting',
        goodDirection: 'higher',
        description: 'Percentage of billable hours billed',
        kpiId: 'realization',
      },
      revenue_per_employee: {
        metric: 'Revenue/Employee',
        benchmark: 180000,
        range: { low: 120000, high: 350000 },
        unit: '$/year',
        source: 'ato',
        sourceNote: 'ATO Small Business Benchmarks',
        goodDirection: 'higher',
        description: 'Annual revenue per full-time employee',
        kpiId: 'rev_employee',
      },
      client_retention: {
        metric: 'Client Retention',
        benchmark: 85,
        range: { low: 70, high: 95 },
        unit: '%',
        source: 'corvana_aggregate',
        sourceNote: 'Corvana professional clients',
        goodDirection: 'higher',
        description: 'Clients retained year-over-year',
        kpiId: 'retention',
      },
      wip_days: {
        metric: 'WIP Days',
        benchmark: 30,
        range: { low: 15, high: 60 },
        unit: 'days',
        source: 'ibisworld',
        sourceNote: 'IBISWorld Working Capital',
        goodDirection: 'lower',
        description: 'Days work-in-progress before billing',
        kpiId: 'wip_days',
      },
    },
  },

  // ========== 8. CONSTRUCTION & TRADES ==========
  construction: {
    industry: 'Construction & Trades',
    atoCode: 'E',
    description: 'Builders, contractors, tradies, construction',
    benchmarks: {
      gross_profit_margin: {
        metric: 'Gross Margin',
        benchmark: 25,
        range: { low: 15, high: 40 },
        unit: '%',
        source: 'ato',
        sourceNote: 'ATO Benchmarks - Construction',
        goodDirection: 'higher',
        description: 'Revenue minus direct job costs',
        kpiId: 'gross_margin',
      },
      on_time_completion: {
        metric: 'On-Time Completion',
        benchmark: 80,
        range: { low: 60, high: 95 },
        unit: '%',
        source: 'hia',
        sourceNote: 'HIA Project Delivery Report',
        goodDirection: 'higher',
        description: 'Projects completed by deadline',
        kpiId: 'on_time',
      },
      safety_incident_rate: {
        metric: 'Incident Rate',
        benchmark: 2.5,
        range: { low: 0.5, high: 5 },
        unit: 'per 100 workers',
        source: 'hia',
        sourceNote: 'Safe Work Australia Statistics',
        goodDirection: 'lower',
        description: 'Recordable incidents per 100 workers',
        kpiId: 'safety',
      },
      quote_win_rate: {
        metric: 'Quote Win Rate',
        benchmark: 30,
        range: { low: 15, high: 50 },
        unit: '%',
        source: 'ibisworld',
        sourceNote: 'IBISWorld Construction Services',
        goodDirection: 'higher',
        description: 'Quotes converted to jobs',
        kpiId: 'win_rate',
      },
      labor_efficiency: {
        metric: 'Labor Efficiency',
        benchmark: 85,
        range: { low: 70, high: 100 },
        unit: '%',
        source: 'hia',
        sourceNote: 'HIA Productivity Benchmarks',
        goodDirection: 'higher',
        description: 'Actual vs estimated labor hours',
        kpiId: 'labor_efficiency',
      },
      subcontractor_cost: {
        metric: 'Subcontractor Cost %',
        benchmark: 35,
        range: { low: 20, high: 55 },
        unit: '%',
        source: 'ato',
        sourceNote: 'ATO Construction Benchmarks',
        goodDirection: 'lower',
        description: 'Subcontractor costs as % of revenue',
        kpiId: 'subbie_cost',
      },
    },
  },

  // ========== 9. FITNESS & WELLNESS ==========
  fitness: {
    industry: 'Fitness & Wellness',
    atoCode: 'R',
    description: 'Gyms, fitness studios, wellness centers, yoga',
    benchmarks: {
      gross_profit_margin: {
        metric: 'Gross Margin',
        benchmark: 55,
        range: { low: 45, high: 70 },
        unit: '%',
        source: 'ato',
        sourceNote: 'ATO Benchmarks - Sports & Recreation',
        goodDirection: 'higher',
        description: 'Revenue minus direct costs',
        kpiId: 'gross_margin',
      },
      member_retention: {
        metric: 'Member Retention',
        benchmark: 70,
        range: { low: 55, high: 85 },
        unit: '%',
        source: 'fitness_australia',
        sourceNote: 'Fitness Australia Industry Report',
        goodDirection: 'higher',
        description: 'Members retained year-over-year',
        kpiId: 'retention',
      },
      revenue_per_member: {
        metric: 'Revenue/Member/Month',
        benchmark: 65,
        range: { low: 40, high: 120 },
        unit: '$/month',
        source: 'fitness_australia',
        sourceNote: 'FA Membership Trends',
        goodDirection: 'higher',
        description: 'Monthly revenue per active member',
        kpiId: 'rev_member',
      },
      class_attendance: {
        metric: 'Class Attendance Rate',
        benchmark: 60,
        range: { low: 40, high: 80 },
        unit: '%',
        source: 'corvana_aggregate',
        sourceNote: 'Corvana fitness clients',
        goodDirection: 'higher',
        description: 'Booked classes attended',
        kpiId: 'attendance',
      },
      labor_cost_percentage: {
        metric: 'Labor Cost %',
        benchmark: 38,
        range: { low: 30, high: 48 },
        unit: '%',
        source: 'fitness_australia',
        sourceNote: 'FA Employment Survey',
        goodDirection: 'lower',
        description: 'Staff wages as % of revenue',
        kpiId: 'labor_cost',
      },
    },
  },

  // ========== 10. BEAUTY & PERSONAL CARE ==========
  beauty: {
    industry: 'Beauty & Personal Care',
    atoCode: 'S',
    description: 'Salons, spas, beauty services, barbershops',
    benchmarks: {
      gross_profit_margin: {
        metric: 'Gross Margin',
        benchmark: 52,
        range: { low: 42, high: 65 },
        unit: '%',
        source: 'ato',
        sourceNote: 'ATO Benchmarks - Personal Services',
        goodDirection: 'higher',
        description: 'Revenue minus product/service costs',
        kpiId: 'gross_margin',
      },
      average_service_value: {
        metric: 'Avg Service Value',
        benchmark: 85,
        range: { low: 45, high: 180 },
        unit: '$',
        source: 'ibisworld',
        sourceNote: 'IBISWorld Hair & Beauty',
        goodDirection: 'higher',
        description: 'Average spend per appointment',
        kpiId: 'avg_service',
      },
      client_retention: {
        metric: 'Client Retention',
        benchmark: 65,
        range: { low: 50, high: 82 },
        unit: '%',
        source: 'corvana_aggregate',
        sourceNote: 'Corvana beauty clients',
        goodDirection: 'higher',
        description: 'Clients returning within 90 days',
        kpiId: 'retention',
      },
      rebooking_rate: {
        metric: 'Rebooking Rate',
        benchmark: 45,
        range: { low: 25, high: 70 },
        unit: '%',
        source: 'corvana_aggregate',
        sourceNote: 'Corvana beauty clients',
        goodDirection: 'higher',
        description: 'Clients who rebook at checkout',
        kpiId: 'rebook',
      },
      product_retail_percentage: {
        metric: 'Retail Product %',
        benchmark: 15,
        range: { low: 8, high: 30 },
        unit: '%',
        source: 'ibisworld',
        sourceNote: 'IBISWorld Hair & Beauty',
        goodDirection: 'higher',
        description: 'Revenue from product sales',
        kpiId: 'retail_pct',
      },
    },
  },

  // ========== 11. AUTOMOTIVE ==========
  automotive: {
    industry: 'Automotive Services',
    atoCode: 'S',
    description: 'Auto repair, mechanics, car dealers, detailing',
    benchmarks: {
      gross_profit_margin: {
        metric: 'Gross Margin',
        benchmark: 48,
        range: { low: 38, high: 60 },
        unit: '%',
        source: 'ato',
        sourceNote: 'ATO Benchmarks - Automotive Repair',
        goodDirection: 'higher',
        description: 'Revenue minus parts and direct labor',
        kpiId: 'gross_margin',
      },
      labor_efficiency: {
        metric: 'Labor Efficiency',
        benchmark: 85,
        range: { low: 70, high: 100 },
        unit: '%',
        source: 'ibisworld',
        sourceNote: 'IBISWorld Motor Vehicle Services',
        goodDirection: 'higher',
        description: 'Billed hours vs clocked hours',
        kpiId: 'labor_efficiency',
      },
      average_repair_order: {
        metric: 'Avg Repair Order',
        benchmark: 450,
        range: { low: 200, high: 850 },
        unit: '$',
        source: 'abs',
        sourceNote: 'ABS Services Survey',
        goodDirection: 'higher',
        description: 'Average revenue per repair order',
        kpiId: 'avg_ro',
      },
      parts_margin: {
        metric: 'Parts Margin',
        benchmark: 35,
        range: { low: 25, high: 50 },
        unit: '%',
        source: 'ato',
        sourceNote: 'ATO Automotive Benchmarks',
        goodDirection: 'higher',
        description: 'Markup on parts sold',
        kpiId: 'parts_margin',
      },
      customer_return_rate: {
        metric: 'Customer Return Rate',
        benchmark: 55,
        range: { low: 35, high: 75 },
        unit: '%',
        source: 'corvana_aggregate',
        sourceNote: 'Corvana auto clients',
        goodDirection: 'higher',
        description: 'Customers returning for service',
        kpiId: 'return_rate',
      },
    },
  },

  // ========== 12. TRANSPORT & LOGISTICS ==========
  transport: {
    industry: 'Transport & Logistics',
    atoCode: 'I',
    description: 'Trucking, courier, freight, logistics',
    benchmarks: {
      gross_profit_margin: {
        metric: 'Gross Margin',
        benchmark: 28,
        range: { low: 18, high: 40 },
        unit: '%',
        source: 'ato',
        sourceNote: 'ATO Benchmarks - Transport',
        goodDirection: 'higher',
        description: 'Revenue minus direct transport costs',
        kpiId: 'gross_margin',
      },
      on_time_delivery: {
        metric: 'On-Time Delivery',
        benchmark: 92,
        range: { low: 85, high: 98 },
        unit: '%',
        source: 'ata',
        sourceNote: 'ATA Performance Report',
        goodDirection: 'higher',
        description: 'Deliveries on time',
        kpiId: 'otd',
      },
      fuel_cost_percentage: {
        metric: 'Fuel Cost %',
        benchmark: 28,
        range: { low: 20, high: 38 },
        unit: '%',
        source: 'ata',
        sourceNote: 'ATA Operating Cost Survey',
        goodDirection: 'lower',
        description: 'Fuel as % of revenue',
        kpiId: 'fuel_cost',
      },
      fleet_utilization: {
        metric: 'Fleet Utilization',
        benchmark: 75,
        range: { low: 60, high: 90 },
        unit: '%',
        source: 'ibisworld',
        sourceNote: 'IBISWorld Road Freight',
        goodDirection: 'higher',
        description: 'Vehicles in active use',
        kpiId: 'fleet_util',
      },
      revenue_per_km: {
        metric: 'Revenue per km',
        benchmark: 2.8,
        range: { low: 1.8, high: 4.5 },
        unit: '$/km',
        source: 'ata',
        sourceNote: 'ATA Rate Benchmarks',
        goodDirection: 'higher',
        description: 'Average revenue per kilometer',
        kpiId: 'rev_km',
      },
    },
  },

  // ========== 13. MANUFACTURING ==========
  manufacturing: {
    industry: 'Manufacturing',
    atoCode: 'C',
    description: 'Manufacturers, producers, fabricators',
    benchmarks: {
      gross_profit_margin: {
        metric: 'Gross Margin',
        benchmark: 32,
        range: { low: 22, high: 45 },
        unit: '%',
        source: 'ato',
        sourceNote: 'ATO Benchmarks - Manufacturing',
        goodDirection: 'higher',
        description: 'Revenue minus COGS',
        kpiId: 'gross_margin',
      },
      oee: {
        metric: 'Overall Equipment Effectiveness',
        benchmark: 65,
        range: { low: 45, high: 85 },
        unit: '%',
        source: 'ibisworld',
        sourceNote: 'IBISWorld Manufacturing',
        goodDirection: 'higher',
        description: 'Availability × Performance × Quality',
        kpiId: 'oee',
      },
      defect_rate: {
        metric: 'Defect Rate',
        benchmark: 2,
        range: { low: 0.5, high: 5 },
        unit: '%',
        source: 'abs',
        sourceNote: 'ABS Manufacturing Survey',
        goodDirection: 'lower',
        description: 'Products with defects',
        kpiId: 'defect',
      },
      inventory_days: {
        metric: 'Inventory Days',
        benchmark: 45,
        range: { low: 25, high: 75 },
        unit: 'days',
        source: 'ato',
        sourceNote: 'ATO Manufacturing Benchmarks',
        goodDirection: 'lower',
        description: 'Days of inventory on hand',
        kpiId: 'inv_days',
      },
      labor_productivity: {
        metric: 'Output per Employee',
        benchmark: 250000,
        range: { low: 150000, high: 450000 },
        unit: '$/year',
        source: 'abs',
        sourceNote: 'ABS Business Indicators',
        goodDirection: 'higher',
        description: 'Annual output per employee',
        kpiId: 'output_employee',
      },
    },
  },

  // ========== 14. FOOD PROCESSING ==========
  food_processing: {
    industry: 'Food Processing',
    atoCode: 'C',
    description: 'Food manufacturers, processors, bakeries',
    benchmarks: {
      gross_profit_margin: {
        metric: 'Gross Margin',
        benchmark: 35,
        range: { low: 25, high: 48 },
        unit: '%',
        source: 'ato',
        sourceNote: 'ATO Benchmarks - Food Manufacturing',
        goodDirection: 'higher',
        description: 'Revenue minus ingredients and direct costs',
        kpiId: 'gross_margin',
      },
      yield_percentage: {
        metric: 'Yield %',
        benchmark: 88,
        range: { low: 78, high: 95 },
        unit: '%',
        source: 'amic',
        sourceNote: 'AMIC Production Standards',
        goodDirection: 'higher',
        description: 'Usable output from raw materials',
        kpiId: 'yield',
      },
      wastage: {
        metric: 'Wastage %',
        benchmark: 5,
        range: { low: 2, high: 12 },
        unit: '%',
        source: 'amic',
        sourceNote: 'AMIC Sustainability Report',
        goodDirection: 'lower',
        description: 'Production waste percentage',
        kpiId: 'wastage',
      },
      food_safety_incidents: {
        metric: 'Safety Incidents',
        benchmark: 0,
        range: { low: 0, high: 2 },
        unit: '/year',
        source: 'amic',
        sourceNote: 'Food Safety Standards',
        goodDirection: 'lower',
        description: 'Food safety incidents per year',
        kpiId: 'safety_incidents',
      },
    },
  },

  // ========== 15. REAL ESTATE ==========
  real_estate: {
    industry: 'Real Estate',
    atoCode: 'L',
    description: 'Property management, agencies, developers',
    benchmarks: {
      gross_profit_margin: {
        metric: 'Gross Margin',
        benchmark: 55,
        range: { low: 40, high: 70 },
        unit: '%',
        source: 'ato',
        sourceNote: 'ATO Benchmarks - Real Estate',
        goodDirection: 'higher',
        description: 'Commission minus direct costs',
        kpiId: 'gross_margin',
      },
      average_days_on_market: {
        metric: 'Days on Market',
        benchmark: 35,
        range: { low: 15, high: 60 },
        unit: 'days',
        source: 'abs',
        sourceNote: 'ABS Property Market Statistics',
        goodDirection: 'lower',
        description: 'Average days to sell property',
        kpiId: 'dom',
      },
      listing_conversion: {
        metric: 'Listing Conversion',
        benchmark: 65,
        range: { low: 45, high: 85 },
        unit: '%',
        source: 'ibisworld',
        sourceNote: 'IBISWorld Real Estate Services',
        goodDirection: 'higher',
        description: 'Appraisals converted to listings',
        kpiId: 'listing_conv',
      },
      rent_roll_retention: {
        metric: 'Rent Roll Retention',
        benchmark: 92,
        range: { low: 85, high: 98 },
        unit: '%',
        source: 'corvana_aggregate',
        sourceNote: 'Corvana real estate clients',
        goodDirection: 'higher',
        description: 'Properties retained in management',
        kpiId: 'rent_retention',
      },
    },
  },

  // ========== 16. EDUCATION & TRAINING ==========
  education: {
    industry: 'Education & Training',
    atoCode: 'P',
    description: 'Training providers, tutors, education services',
    benchmarks: {
      gross_profit_margin: {
        metric: 'Gross Margin',
        benchmark: 48,
        range: { low: 35, high: 65 },
        unit: '%',
        source: 'ato',
        sourceNote: 'ATO Benchmarks - Education',
        goodDirection: 'higher',
        description: 'Revenue minus direct delivery costs',
        kpiId: 'gross_margin',
      },
      course_completion: {
        metric: 'Completion Rate',
        benchmark: 75,
        range: { low: 55, high: 92 },
        unit: '%',
        source: 'ibisworld',
        sourceNote: 'IBISWorld Education & Training',
        goodDirection: 'higher',
        description: 'Students completing courses',
        kpiId: 'completion',
      },
      student_satisfaction: {
        metric: 'Student Satisfaction',
        benchmark: 85,
        range: { low: 70, high: 95 },
        unit: '%',
        source: 'corvana_aggregate',
        sourceNote: 'Corvana education clients',
        goodDirection: 'higher',
        description: 'Student satisfaction score',
        kpiId: 'satisfaction',
      },
      revenue_per_student: {
        metric: 'Revenue/Student',
        benchmark: 1200,
        range: { low: 500, high: 5000 },
        unit: '$/student',
        source: 'abs',
        sourceNote: 'ABS Education Services',
        goodDirection: 'higher',
        description: 'Average revenue per enrolled student',
        kpiId: 'rev_student',
      },
    },
  },

  // ========== 17. CHILDCARE ==========
  childcare: {
    industry: 'Childcare Services',
    atoCode: 'P',
    description: 'Childcare centers, early learning, family daycare',
    benchmarks: {
      occupancy_rate: {
        metric: 'Occupancy Rate',
        benchmark: 85,
        range: { low: 70, high: 95 },
        unit: '%',
        source: 'ibisworld',
        sourceNote: 'IBISWorld Childcare Services',
        goodDirection: 'higher',
        description: 'Licensed places filled',
        kpiId: 'occupancy',
      },
      gross_profit_margin: {
        metric: 'Gross Margin',
        benchmark: 28,
        range: { low: 18, high: 40 },
        unit: '%',
        source: 'ato',
        sourceNote: 'ATO Benchmarks - Childcare',
        goodDirection: 'higher',
        description: 'Revenue minus direct costs',
        kpiId: 'gross_margin',
      },
      staff_to_child_ratio: {
        metric: 'Staff:Child Ratio',
        benchmark: 4,
        range: { low: 3, high: 5 },
        unit: ':1',
        source: 'ahpra',
        sourceNote: 'National Quality Framework',
        goodDirection: 'lower',
        description: 'Children per staff member',
        kpiId: 'ratio',
      },
      family_retention: {
        metric: 'Family Retention',
        benchmark: 80,
        range: { low: 65, high: 92 },
        unit: '%',
        source: 'corvana_aggregate',
        sourceNote: 'Corvana childcare clients',
        goodDirection: 'higher',
        description: 'Families retained year-over-year',
        kpiId: 'retention',
      },
    },
  },

  // ========== 18. AGED CARE ==========
  aged_care: {
    industry: 'Aged Care Services',
    atoCode: 'Q',
    description: 'Residential aged care, home care, disability',
    benchmarks: {
      occupancy_rate: {
        metric: 'Bed Occupancy',
        benchmark: 92,
        range: { low: 85, high: 98 },
        unit: '%',
        source: 'ibisworld',
        sourceNote: 'IBISWorld Aged Care',
        goodDirection: 'higher',
        description: 'Beds/places filled',
        kpiId: 'occupancy',
      },
      staff_turnover: {
        metric: 'Staff Turnover',
        benchmark: 25,
        range: { low: 12, high: 40 },
        unit: '%/year',
        source: 'abs',
        sourceNote: 'ABS Labour Force - Health',
        goodDirection: 'lower',
        description: 'Annual staff turnover rate',
        kpiId: 'turnover',
      },
      care_minutes_per_day: {
        metric: 'Care Minutes/Day',
        benchmark: 200,
        range: { low: 180, high: 240 },
        unit: 'min',
        source: 'ahpra',
        sourceNote: 'Aged Care Quality Standards',
        goodDirection: 'higher',
        description: 'Direct care minutes per resident',
        kpiId: 'care_mins',
      },
      compliance_rating: {
        metric: 'Compliance Rating',
        benchmark: 95,
        range: { low: 85, high: 100 },
        unit: '%',
        source: 'ahpra',
        sourceNote: 'Quality Standards Assessment',
        goodDirection: 'higher',
        description: 'Regulatory compliance score',
        kpiId: 'compliance',
      },
    },
  },

  // ========== 19. AGRICULTURE ==========
  agriculture: {
    industry: 'Agriculture',
    atoCode: 'A',
    description: 'Farming, livestock, horticulture, nurseries',
    benchmarks: {
      gross_profit_margin: {
        metric: 'Gross Margin',
        benchmark: 35,
        range: { low: 20, high: 55 },
        unit: '%',
        source: 'ato',
        sourceNote: 'ATO Benchmarks - Agriculture',
        goodDirection: 'higher',
        description: 'Revenue minus direct costs',
        kpiId: 'gross_margin',
      },
      yield_per_hectare: {
        metric: 'Yield/Hectare',
        benchmark: 100,
        range: { low: 50, high: 200 },
        unit: '%',
        source: 'abs',
        sourceNote: 'ABS Agricultural Survey',
        goodDirection: 'higher',
        description: 'Yield vs industry average',
        kpiId: 'yield',
      },
      input_cost_ratio: {
        metric: 'Input Cost %',
        benchmark: 45,
        range: { low: 30, high: 60 },
        unit: '%',
        source: 'abs',
        sourceNote: 'ABS Farm Input Costs',
        goodDirection: 'lower',
        description: 'Input costs as % of revenue',
        kpiId: 'input_cost',
      },
    },
  },

  // ========== 20. MINING SERVICES ==========
  mining_services: {
    industry: 'Mining Services',
    atoCode: 'B',
    description: 'Mining contractors, exploration, equipment',
    benchmarks: {
      gross_profit_margin: {
        metric: 'Gross Margin',
        benchmark: 28,
        range: { low: 18, high: 42 },
        unit: '%',
        source: 'ato',
        sourceNote: 'ATO Benchmarks - Mining Services',
        goodDirection: 'higher',
        description: 'Revenue minus direct costs',
        kpiId: 'gross_margin',
      },
      equipment_utilization: {
        metric: 'Equipment Utilization',
        benchmark: 75,
        range: { low: 60, high: 90 },
        unit: '%',
        source: 'ibisworld',
        sourceNote: 'IBISWorld Mining Services',
        goodDirection: 'higher',
        description: 'Equipment in active use',
        kpiId: 'equip_util',
      },
      safety_rate: {
        metric: 'LTIFR',
        benchmark: 3,
        range: { low: 0.5, high: 8 },
        unit: 'per million hours',
        source: 'abs',
        sourceNote: 'Safe Work Australia',
        goodDirection: 'lower',
        description: 'Lost time injury frequency rate',
        kpiId: 'ltifr',
      },
    },
  },

  // ========== 21. TOURISM & ACCOMMODATION ==========
  tourism: {
    industry: 'Tourism & Accommodation',
    atoCode: 'H',
    description: 'Hotels, motels, caravan parks, tour operators',
    benchmarks: {
      occupancy_rate: {
        metric: 'Occupancy Rate',
        benchmark: 68,
        range: { low: 50, high: 85 },
        unit: '%',
        source: 'abs',
        sourceNote: 'ABS Tourism Satellite Account',
        goodDirection: 'higher',
        description: 'Rooms/sites filled',
        kpiId: 'occupancy',
      },
      revpar: {
        metric: 'RevPAR',
        benchmark: 125,
        range: { low: 65, high: 250 },
        unit: '$/room',
        source: 'ibisworld',
        sourceNote: 'IBISWorld Accommodation',
        goodDirection: 'higher',
        description: 'Revenue per available room',
        kpiId: 'revpar',
      },
      adr: {
        metric: 'Avg Daily Rate',
        benchmark: 185,
        range: { low: 95, high: 350 },
        unit: '$',
        source: 'abs',
        sourceNote: 'ABS Accommodation Survey',
        goodDirection: 'higher',
        description: 'Average room rate',
        kpiId: 'adr',
      },
      gross_profit_margin: {
        metric: 'GOP Margin',
        benchmark: 35,
        range: { low: 22, high: 50 },
        unit: '%',
        source: 'ato',
        sourceNote: 'ATO Benchmarks - Accommodation',
        goodDirection: 'higher',
        description: 'Gross operating profit margin',
        kpiId: 'gop',
      },
    },
  },

  // ========== 22. MEDIA & CREATIVE ==========
  media: {
    industry: 'Media & Creative Services',
    atoCode: 'J',
    description: 'Advertising, design, media production, marketing',
    benchmarks: {
      gross_profit_margin: {
        metric: 'Gross Margin',
        benchmark: 55,
        range: { low: 40, high: 72 },
        unit: '%',
        source: 'ato',
        sourceNote: 'ATO Benchmarks - Media',
        goodDirection: 'higher',
        description: 'Revenue minus direct costs',
        kpiId: 'gross_margin',
      },
      utilization_rate: {
        metric: 'Utilization Rate',
        benchmark: 70,
        range: { low: 55, high: 85 },
        unit: '%',
        source: 'ibisworld',
        sourceNote: 'IBISWorld Advertising Services',
        goodDirection: 'higher',
        description: 'Billable hours percentage',
        kpiId: 'utilization',
      },
      client_retention: {
        metric: 'Client Retention',
        benchmark: 75,
        range: { low: 60, high: 90 },
        unit: '%',
        source: 'corvana_aggregate',
        sourceNote: 'Corvana creative clients',
        goodDirection: 'higher',
        description: 'Clients retained year-over-year',
        kpiId: 'retention',
      },
      revenue_per_fte: {
        metric: 'Revenue/FTE',
        benchmark: 165000,
        range: { low: 100000, high: 280000 },
        unit: '$/year',
        source: 'abs',
        sourceNote: 'ABS Business Indicators',
        goodDirection: 'higher',
        description: 'Annual revenue per employee',
        kpiId: 'rev_fte',
      },
    },
  },

  // ========== 23. TECH & IT SERVICES ==========
  technology: {
    industry: 'Technology & IT Services',
    atoCode: 'J',
    description: 'IT services, software, MSPs, tech consulting',
    benchmarks: {
      gross_profit_margin: {
        metric: 'Gross Margin',
        benchmark: 62,
        range: { low: 48, high: 78 },
        unit: '%',
        source: 'ato',
        sourceNote: 'ATO Benchmarks - IT Services',
        goodDirection: 'higher',
        description: 'Revenue minus COGS',
        kpiId: 'gross_margin',
      },
      recurring_revenue_percentage: {
        metric: 'Recurring Revenue %',
        benchmark: 65,
        range: { low: 40, high: 90 },
        unit: '%',
        source: 'ibisworld',
        sourceNote: 'IBISWorld IT Services',
        goodDirection: 'higher',
        description: 'MRR/ARR as % of total revenue',
        kpiId: 'mrr_pct',
      },
      customer_churn: {
        metric: 'Customer Churn',
        benchmark: 8,
        range: { low: 2, high: 15 },
        unit: '%/year',
        source: 'corvana_aggregate',
        sourceNote: 'Corvana tech clients',
        goodDirection: 'lower',
        description: 'Annual customer churn rate',
        kpiId: 'churn',
      },
      nps: {
        metric: 'Net Promoter Score',
        benchmark: 45,
        range: { low: 20, high: 70 },
        unit: '',
        source: 'corvana_ai',
        sourceNote: 'Corvana survey analysis',
        goodDirection: 'higher',
        description: 'Customer loyalty score',
        kpiId: 'nps',
      },
    },
  },

  // ========== 24. FINANCIAL SERVICES ==========
  financial_services: {
    industry: 'Financial Services',
    atoCode: 'K',
    description: 'Brokers, advisors, finance companies',
    benchmarks: {
      gross_profit_margin: {
        metric: 'Gross Margin',
        benchmark: 68,
        range: { low: 52, high: 82 },
        unit: '%',
        source: 'ato',
        sourceNote: 'ATO Benchmarks - Financial Services',
        goodDirection: 'higher',
        description: 'Revenue minus direct costs',
        kpiId: 'gross_margin',
      },
      client_retention: {
        metric: 'Client Retention',
        benchmark: 88,
        range: { low: 75, high: 96 },
        unit: '%',
        source: 'ibisworld',
        sourceNote: 'IBISWorld Financial Planning',
        goodDirection: 'higher',
        description: 'Clients retained year-over-year',
        kpiId: 'retention',
      },
      revenue_per_advisor: {
        metric: 'Revenue/Advisor',
        benchmark: 320000,
        range: { low: 180000, high: 650000 },
        unit: '$/year',
        source: 'abs',
        sourceNote: 'ABS Financial Services',
        goodDirection: 'higher',
        description: 'Annual revenue per advisor',
        kpiId: 'rev_advisor',
      },
      compliance_score: {
        metric: 'Compliance Score',
        benchmark: 95,
        range: { low: 88, high: 100 },
        unit: '%',
        source: 'corvana_ai',
        sourceNote: 'Corvana compliance analysis',
        goodDirection: 'higher',
        description: 'Regulatory compliance rating',
        kpiId: 'compliance',
      },
    },
  },

  // ========== DEFAULT (General Business) ==========
  default: {
    industry: 'General Business',
    atoCode: 'ALL',
    description: 'Balanced benchmarks for all business types',
    benchmarks: {
      gross_profit_margin: {
        metric: 'Gross Margin',
        benchmark: 40,
        range: { low: 25, high: 60 },
        unit: '%',
        source: 'ato',
        sourceNote: 'ATO Small Business Benchmarks (All Industries)',
        goodDirection: 'higher',
        description: 'Revenue minus cost of goods sold',
        kpiId: 'gross_margin',
      },
      net_profit_margin: {
        metric: 'Net Profit Margin',
        benchmark: 10,
        range: { low: 5, high: 20 },
        unit: '%',
        source: 'ato',
        sourceNote: 'ATO Small Business Benchmarks',
        goodDirection: 'higher',
        description: 'Net profit as percentage of revenue',
        kpiId: 'net_margin',
      },
      current_ratio: {
        metric: 'Current Ratio',
        benchmark: 1.5,
        range: { low: 1.0, high: 2.5 },
        unit: 'x',
        source: 'abs',
        sourceNote: 'ABS Business Indicators Survey',
        goodDirection: 'higher',
        description: 'Current assets divided by current liabilities',
        kpiId: 'current_ratio',
      },
      revenue_growth: {
        metric: 'Revenue Growth',
        benchmark: 8,
        range: { low: 0, high: 25 },
        unit: '%/year',
        source: 'abs',
        sourceNote: 'ABS Business Growth Survey',
        goodDirection: 'higher',
        description: 'Year-over-year revenue increase',
        kpiId: 'revenue_growth',
      },
      customer_retention: {
        metric: 'Customer Retention',
        benchmark: 75,
        range: { low: 60, high: 90 },
        unit: '%',
        source: 'corvana_aggregate',
        sourceNote: 'Corvana client average',
        goodDirection: 'higher',
        description: 'Customers who return within 12 months',
        kpiId: 'retention',
      },
      employee_productivity: {
        metric: 'Revenue/Employee',
        benchmark: 150000,
        range: { low: 80000, high: 300000 },
        unit: '$/year',
        source: 'abs',
        sourceNote: 'ABS Business Indicators',
        goodDirection: 'higher',
        description: 'Annual revenue per employee',
        kpiId: 'rev_employee',
      },
    },
  },
};

// ============================================
// HELPER FUNCTIONS
// ============================================

// Map common industry variations to benchmark keys
const INDUSTRY_MAP = {
  // Hospitality
  'hospitality': 'hospitality',
  'food_beverage': 'hospitality',
  'restaurant': 'hospitality',
  'cafe': 'hospitality',
  'bar': 'hospitality',
  'catering': 'hospitality',
  
  // Healthcare
  'healthcare': 'healthcare',
  'medical': 'healthcare',
  'dental': 'healthcare',
  'allied_health': 'healthcare',
  'pharmacy': 'healthcare',
  
  // Veterinary
  'veterinary': 'veterinary',
  'vet': 'veterinary',
  'animal_care': 'veterinary',
  
  // Wholesale
  'wholesale': 'wholesale',
  'wholesale_distribution': 'wholesale',
  'distribution': 'wholesale',
  'b2b': 'wholesale',
  
  // Retail
  'retail': 'retail',
  'shop': 'retail',
  'store': 'retail',
  
  // E-commerce
  'ecommerce': 'ecommerce',
  'e_commerce': 'ecommerce',
  'online_retail': 'ecommerce',
  
  // Professional Services
  'professional': 'professional',
  'professional_services': 'professional',
  'legal': 'professional',
  'accounting': 'professional',
  'consulting': 'professional',
  'advisory': 'professional',
  
  // Construction
  'construction': 'construction',
  'trades': 'construction',
  'building': 'construction',
  'plumbing': 'construction',
  'electrical': 'construction',
  
  // Fitness
  'fitness': 'fitness',
  'gym': 'fitness',
  'wellness': 'fitness',
  'yoga': 'fitness',
  'pilates': 'fitness',
  
  // Beauty
  'beauty': 'beauty',
  'salon': 'beauty',
  'spa': 'beauty',
  'hair': 'beauty',
  'barbershop': 'beauty',
  
  // Automotive
  'automotive': 'automotive',
  'mechanic': 'automotive',
  'auto_repair': 'automotive',
  'car_dealer': 'automotive',
  
  // Transport
  'transport': 'transport',
  'logistics': 'transport',
  'trucking': 'transport',
  'courier': 'transport',
  'freight': 'transport',
  
  // Manufacturing
  'manufacturing': 'manufacturing',
  'production': 'manufacturing',
  'fabrication': 'manufacturing',
  
  // Food Processing
  'food_processing': 'food_processing',
  'food_manufacturing': 'food_processing',
  'bakery': 'food_processing',
  
  // Real Estate
  'real_estate': 'real_estate',
  'property': 'real_estate',
  'property_management': 'real_estate',
  
  // Education
  'education': 'education',
  'training': 'education',
  'tutoring': 'education',
  
  // Childcare
  'childcare': 'childcare',
  'early_learning': 'childcare',
  'daycare': 'childcare',
  
  // Aged Care
  'aged_care': 'aged_care',
  'disability': 'aged_care',
  'ndis': 'aged_care',
  
  // Agriculture
  'agriculture': 'agriculture',
  'farming': 'agriculture',
  'horticulture': 'agriculture',
  
  // Mining
  'mining': 'mining_services',
  'mining_services': 'mining_services',
  'resources': 'mining_services',
  
  // Tourism
  'tourism': 'tourism',
  'accommodation': 'tourism',
  'hotel': 'tourism',
  'motel': 'tourism',
  
  // Media
  'media': 'media',
  'creative': 'media',
  'advertising': 'media',
  'marketing': 'media',
  'design': 'media',
  
  // Technology / IT services / ICT (ANZSIC division 'media_telecom')
  'technology': 'technology',
  'it': 'technology',
  'it_services': 'technology',
  'information_technology': 'technology',
  'ict': 'technology',
  'software': 'technology',
  'software_development': 'technology',
  'saas': 'technology',
  'tech': 'technology',
  'msp': 'technology',
  'managed_services': 'technology',
  'managed_service_provider': 'technology',
  'computer': 'technology',
  'computer_technology': 'technology',
  'media_telecom': 'media',
  'media_telecommunications': 'media',
  'information_media': 'media',
  'information_media_telecommunications': 'media',
  'information_media_and_telecommunications': 'media',
  'telecommunications': 'media',
  'telecom': 'media',

  // Agencies (creative / digital / marketing / advertising → media benchmarks)
  'agency': 'media',
  'digital_agency': 'media',
  'creative_agency': 'media',
  'advertising_agency': 'media',
  'marketing_agency': 'media',
  'ad_agency': 'media',
  'pr_agency': 'media',
  'branding': 'media',

  // Financial Services
  'financial': 'financial_services',
  'financial_services': 'financial_services',
  'finance': 'financial_services',
  'broker': 'financial_services',
  'advisor': 'financial_services',
};

// Get benchmarks for an industry
export const getIndustryBenchmarks = (industry) => {
  const normalizedIndustry = industry?.toLowerCase().replace(/[^a-z_]/g, '_').replace(/_+/g, '_');
  const mappedIndustry = INDUSTRY_MAP[normalizedIndustry] || 'default';
  return INDUSTRY_BENCHMARKS[mappedIndustry] || INDUSTRY_BENCHMARKS.default;
};

// Compare value against benchmark
export const compareToBenchmark = (value, benchmarkConfig) => {
  const { benchmark, range, goodDirection } = benchmarkConfig;
  
  let status = 'average';
  let percentDiff = ((value - benchmark) / benchmark) * 100;
  
  if (goodDirection === 'higher') {
    if (value >= range.high) status = 'excellent';
    else if (value >= benchmark) status = 'good';
    else if (value >= range.low) status = 'average';
    else status = 'poor';
  } else if (goodDirection === 'lower') {
    if (value <= range.low) status = 'excellent';
    else if (value <= benchmark) status = 'good';
    else if (value <= range.high) status = 'average';
    else status = 'poor';
    percentDiff = -percentDiff;
  } else {
    if (value >= range.low && value <= range.high) status = 'good';
    else status = 'average';
  }
  
  return {
    status,
    percentDiff: Math.round(percentDiff),
    vsIndustry: percentDiff > 0 ? 'above' : percentDiff < 0 ? 'below' : 'at',
  };
};

// Get all industry names for dropdowns
export const getAllIndustries = () => {
  return Object.entries(INDUSTRY_BENCHMARKS)
    .filter(([key]) => key !== 'default')
    .map(([key, value]) => ({
      id: key,
      name: value.industry,
      atoCode: value.atoCode,
      description: value.description,
    }));
};

export default {
  DATA_SOURCES,
  INDUSTRY_BENCHMARKS,
  getIndustryBenchmarks,
  compareToBenchmark,
  getAllIndustries,
};
